import React from 'react';
import { useTranslation } from 'react-i18next';
import './ContractMessage.css';

interface ContractData {
  title: string;
  description: string;
  price?: number;
  finalAmount?: number;
  startDate: string;
  endDate: string;
  deliverables?: string;
  projectId?: number;
  serviceId?: number;
  createdAt: string;
  clientAgreed?: boolean;
  freelancerAgreed?: boolean;
  clientAgreedAt?: string;
  freelancerAgreedAt?: string;
}

interface ContractMessageProps {
  messageId: number;
  contractData: ContractData;
  isMyMessage: boolean;
  isClient: boolean;
  clientAgreed: boolean;
  freelancerAgreed: boolean;
  onAgree?: (messageId: number) => void;
  onReject?: (messageId: number) => void;
}

const ContractMessage: React.FC<ContractMessageProps> = ({
  messageId,
  contractData,
  isMyMessage,
  isClient,
  clientAgreed,
  freelancerAgreed,
  onAgree,
  onReject
}) => {
  const { t, i18n } = useTranslation();
  const bothAgreed = clientAgreed && freelancerAgreed;
  const myAgreed = isClient ? clientAgreed : freelancerAgreed;
  const otherAgreed = isClient ? freelancerAgreed : clientAgreed;

  const getStatusText = () => {
    if (bothAgreed) return t('contractMsg.bothAgreed');
    if (myAgreed && !otherAgreed) return t('contractMsg.waitingOther');
    if (!myAgreed && otherAgreed) return t('contractMsg.needMyAgree');
    return t('contractMsg.needBoth');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getDaysCount = () => {
    const start = new Date(contractData.startDate);
    const end = new Date(contractData.endDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className={`contract-message ${bothAgreed ? 'agreed' : ''}`}>
      <div className="contract-header">
        <span className="contract-icon">📝</span>
        <span className="contract-title">{t('contractMsg.title')}</span>
        {bothAgreed && <span className="contract-badge">{t('contractMsg.sealed')}</span>}
      </div>

      <div className="contract-status-bar">
        {getStatusText()}
      </div>

      <div className="contract-content">
        <h3>{contractData.title}</h3>
        
        {contractData.description && (
          <p className="contract-description">{contractData.description}</p>
        )}

        <div className="contract-details">
          <div className="detail-row">
            <span className="label">💰 {t('contractModal.totalPrice')}</span>
            <span className="value price">{t('projects.budgetExact', { v: (contractData.price || contractData.finalAmount || 0).toLocaleString() })}</span>
          </div>
          <div className="detail-row">
            <span className="label">📅 {t('contractMsg.startDate')}</span>
            <span className="value">{contractData.startDate ? formatDate(contractData.startDate) : '-'}</span>
          </div>
          <div className="detail-row">
            <span className="label">📅 {t('contractMsg.endDate')}</span>
            <span className="value">{contractData.endDate ? formatDate(contractData.endDate) : '-'}</span>
          </div>
          <div className="detail-row">
            <span className="label">⏱️ {t('contractModal.period')}</span>
            <span className="value">{contractData.startDate && contractData.endDate ? t('projectDetail.daysN', { n: getDaysCount() }) : '-'}</span>
          </div>
          {contractData.deliverables && (
            <div className="detail-row full">
              <span className="label">📦 {t('contractMsg.deliverables')}</span>
              <span className="value">{contractData.deliverables}</span>
            </div>
          )}
        </div>
      </div>

      <div className="contract-agree-status">
        <div className={`agree-item ${freelancerAgreed ? 'agreed' : ''}`}>
          <span className="role">{t('nav.expert')}</span>
          <span className="status">{freelancerAgreed ? t('contractMsg.agreedShort') : t('contractMsg.waitingShort')}</span>
        </div>
        <div className={`agree-item ${clientAgreed ? 'agreed' : ''}`}>
          <span className="role">{t('projects.client')}</span>
          <span className="status">{clientAgreed ? t('contractMsg.agreedShort') : t('contractMsg.waitingShort')}</span>
        </div>
      </div>

      {!bothAgreed && !myAgreed && (
        <div className="contract-actions">
          <button className="btn-reject" onClick={() => onReject && onReject(messageId)}>
            {t('common.reject')}
          </button>
          <button className="btn-agree" onClick={() => onAgree && onAgree(messageId)}>
            {t('contractMsg.agree')}
          </button>
        </div>
      )}

      {bothAgreed && (
        <div className="contract-started">
          🎉 {t('contractMsg.started')}
        </div>
      )}
    </div>
  );
};

export default ContractMessage;
